import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { BreadcrumbItem } from "components/Breadcrumbs/Breadcrumbs";
import { Typography, Box, Button, Alert, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ReportJob, MetricsService, ReportJobStatus, ReportFileInfo } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { formatDate } from "utils/dateUtils";
import { ClientContactsReportTable, ClientContactsReportFilters } from "./ClientContactsReportTable";
import { ExportClientContactsReportButton } from "./ExportClientContactsReportButton";
import { ClientContactsReportFilter } from "./ClientContactsReportFilter";
import { useSessionStorage } from "usehooks-ts";

interface RouteParams {
  reportId: string;
}

const ClientContactsReportDetailsPage: React.FC = () => {
  const { reportId } = useParams<RouteParams>();
  const history = useHistory();

  // Use session storage to persist filter state when navigating back to the page
  const [filters, setFilters] = useSessionStorage<ClientContactsReportFilters>(
    `clientContactsReportFilters_${reportId}`,
    {}
  );

  const {
    data: report,
    isLoading,
    error
  } = useQuery<ReportJob>(["getClientContactsReport", reportId], {
    queryFn: () => MetricsService.getReportJob({ reportId }),
    refetchInterval: data => {
      // Refetch every 5 seconds if the report is still running
      return data?.status === ReportJobStatus.PENDING || data?.status === ReportJobStatus.RUNNING ? 5000 : false;
    }
  });

  // Fetch report data for generating filter options
  const jsonFile = React.useMemo(() => {
    return report?.files?.find((file: ReportFileInfo) => file.format === ReportFileInfo.format.JSON);
  }, [report?.files]);

  const { data: reportData } = useQuery<any[]>(["clientContactsReportData", reportId, jsonFile?.s3Key], {
    queryFn: async () => {
      if (!jsonFile?.s3Key) {
        console.warn("JSON file s3Key is missing:", { jsonFile, reportFiles: report?.files });
        return [];
      }

      // Get secure download URL
      const downloadResponse = await MetricsService.downloadReportJobFile({
        reportId,
        s3Key: encodeURIComponent(jsonFile.s3Key)
      });

      // Fetch the actual data
      const response = await fetch(downloadResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!jsonFile?.s3Key && report?.status === ReportJobStatus.COMPLETED,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000 // 10 minutes
  });

  const handleFiltersChange = React.useCallback(
    (newFilters: ClientContactsReportFilters) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const breadcrumbs: BreadcrumbItem[] = [
    {
      path: "/analytics",
      label: "Analytics"
    },
    {
      path: "/analytics/clientContactsReports",
      label: "Client Contacts Report"
    },
    {
      path: `/analytics/clientContactsReports/${reportId}`,
      label: `Report ${reportId}`,
      active: true
    }
  ];

  const handleBackClick = React.useCallback(() => {
    history.push("/analytics/clientContactsReports");
  }, [history]);

  const csvFile = React.useMemo(() => {
    return report?.files?.find((file: ReportFileInfo) => file.format === ReportFileInfo.format.CSV);
  }, [report?.files]);

  if (isLoading) {
    return (
      <PageLayout
        selectedNavMenu={NavMenuOption.ANALYTICS}
        title={<Typography variant="h6">Loading Report...</Typography>}
        breadcrumbs={breadcrumbs}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        selectedNavMenu={NavMenuOption.ANALYTICS}
        title={<Typography variant="h6">Report Error</Typography>}
        breadcrumbs={breadcrumbs}
      >
        <ApiErrorMessage apiError={error} />
      </PageLayout>
    );
  }

  if (!report) {
    return (
      <PageLayout
        selectedNavMenu={NavMenuOption.ANALYTICS}
        title={<Typography variant="h6">Report Not Found</Typography>}
        breadcrumbs={breadcrumbs}
      >
        <Alert severity="error">Report not found</Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={<Typography variant="h6">Client Contacts Report - {reportId}</Typography>}
      breadcrumbs={breadcrumbs}
    >
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mb: 2 }}>
          Back to Reports
        </Button>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {report.status}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Created by:</strong> {report.createdBy}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Created at:</strong> {formatDate(report.createdAt)}
            </Typography>
            {report.completedAt && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Completed at:</strong> {formatDate(report.completedAt)}
              </Typography>
            )}
          </Box>

          {report.status === ReportJobStatus.COMPLETED && csvFile && (
            <ExportClientContactsReportButton reportId={reportId} csvFile={csvFile} />
          )}
        </Box>

        {report.status === ReportJobStatus.FAILED && report.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Error:</strong> {report.error.message}
            </Typography>
          </Alert>
        )}

        {(report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Report is being generated. This page will automatically refresh when the report is ready.
            </Typography>
          </Alert>
        )}
      </Box>

      {report.status === ReportJobStatus.COMPLETED && jsonFile && jsonFile.s3Key && (
        <>
          <ClientContactsReportFilter
            initialFilters={filters}
            onFiltersChange={handleFiltersChange}
            reportData={reportData}
          />
          <ClientContactsReportTable reportId={reportId} jsonFile={jsonFile} filters={filters} />
        </>
      )}

      {report.status === ReportJobStatus.COMPLETED && (!jsonFile || !jsonFile.s3Key) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Report data is not available for viewing. The JSON file may not have been generated properly.
          </Typography>
        </Alert>
      )}
    </PageLayout>
  );
};

export { ClientContactsReportDetailsPage };
